/**
 * `AudioContext.resume()` acotado — nunca cuelga el arranque de la sesión.
 *
 * Por qué existe (bug de campo 2026-09-24, "el coach está pensando" para
 * siempre): bajo la política de autoplay de Chrome, un `resume()` hecho SIN
 * activación del user (recarga de /conversation, relanzar la PWA, full-reload
 * de Vite, entrar por URL) queda PENDIENTE — no rechaza — y esa promesa no se
 * resuelve sola ni con un click posterior: sólo un `resume()` NUEVO llamado con
 * activación la destraba. `AudioPlayer.unlock()` y el bootstrap del mic la
 * esperaban antes de abrir el WS, así que el socket nunca abría.
 *
 * Acá esperamos como mucho `RESUME_WAIT_MS` (en el caso sano el resume tarda
 * decenas de ms) y, si el browser sigue bloqueando, devolvemos `'blocked'` y
 * dejamos armado un re-intento en el PRÓXIMO gesto del user (el tap del mic en
 * modo hold, cualquier tap en la pantalla): ese `resume()` sí tiene activación
 * y arranca el ctx — lo que llegó mientras tanto (el opener) ya está encolado
 * y suena ahí. Nada queda en silencio sin aviso: se loguea el bloqueo.
 */

/** Tope de espera del `resume()` antes de seguir sin audio arrancado. */
export const RESUME_WAIT_MS = 1500;

export type ResumeResult = 'running' | 'blocked';

/** Gestos que dan activación de user (touchend: iOS no la da en touchstart). */
const GESTURE_EVENTS = ['pointerdown', 'keydown', 'touchend'] as const;

function armResumeOnGesture(ctx: AudioContext, label: string): void {
  const release = (): void => {
    for (const ev of GESTURE_EVENTS) window.removeEventListener(ev, onGesture, true);
    ctx.removeEventListener('statechange', onStateChange);
  };
  const onGesture = (): void => {
    if (ctx.state !== 'suspended') {
      release();
      return;
    }
    void ctx.resume().catch(() => {
      /* sin activación suficiente: el próximo gesto reintenta */
    });
  };
  const onStateChange = (): void => {
    if (ctx.state !== 'suspended') release();
  };
  for (const ev of GESTURE_EVENTS) window.addEventListener(ev, onGesture, true);
  ctx.addEventListener('statechange', onStateChange);
  console.warn(`[voice] ${label}: AudioContext bloqueado por autoplay — arranca con el próximo gesto`);
}

export async function resumeAudioContext(
  ctx: AudioContext,
  label = 'audio',
): Promise<ResumeResult> {
  if (ctx.state !== 'suspended') return 'running';
  let timer: ReturnType<typeof setTimeout> | null = null;
  const resumed = ctx.resume().then(
    () => 'resumed' as const,
    () => 'rejected' as const,
  );
  const timedOut = new Promise<'timeout'>((res) => {
    timer = setTimeout(() => res('timeout'), RESUME_WAIT_MS);
  });
  await Promise.race([resumed, timedOut]);
  if (timer) clearTimeout(timer);
  // `state` es la verdad: un resume que "resolvió" pero dejó el ctx
  // suspendido (o uno que rechazó) también cuenta como bloqueado.
  if ((ctx.state as AudioContextState) !== 'suspended') return 'running';
  if (typeof window !== 'undefined') armResumeOnGesture(ctx, label);
  return 'blocked';
}
